import { useState, useMemo, useEffect, useLayoutEffect, useRef } from 'react';
import {
  Bot,
  Bug,
  Workflow,
  Pencil,
  ArrowLeft,
  ArrowUp,
  AlertTriangle,
  Settings2,
  LayoutGrid,
  Loader2,
  Plus,
  X,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { GALLERY_TEMPLATES } from '@reflex/flows';
import { isPersistableModelProvider } from '@reflex/shared';
import type { ChatModel, ModelProvider } from '@reflex/shared';
import type { FlowDefinition, FlowNodeType, GalleryTemplateId } from '@reflex/flows';
import { Badge } from '@reflex/ui/components/ui/badge';
import { Button } from '@reflex/ui/components/ui/button';
import { Textarea } from '@reflex/ui/components/ui/textarea';
import {
  PageDescription,
  PageTitle,
  PanelTitle,
  SectionLabel,
} from '@reflex/ui/components/layout/Typography';
import { SELECTABLE_MODELS, MODEL_MAP } from '@reflex/ui/lib/model-icons';
import { ModelProviderSecretPicker } from '@reflex/ui/components/model-providers/ModelProviderSecretPicker';
import { ModelSelector } from '@reflex/plugin-chat/web/pages/ModelSelector';
import { FlowGraph } from './FlowGraph';
import { PluginIcon } from '@reflex/ui/components/shared/PluginIcon';
import { SpecificationPicker, SPEC_KIND_ICONS } from './SpecificationPicker';
import { flowsApi } from '../api';
import type { FlowTemplateMissingPlugin } from '../../shared/templates';
import { specificationsPromptSuffix, type Specification } from '../../shared/specifications';
import { useOrgRoutes, useOrgKey } from '@reflex/ui/client/context';
import { useEnabledModels } from '@reflex/ui/client/hooks';
import { cn } from '@reflex/ui/lib/utils';
/**
 * Config for the model + provider-key pickers shown above the
 * "describe your flow" generate box. Mirrors `EditChatPanel`'s
 * `modelPicker` so the chosen model and remembered key carry straight
 * into the editor chat that auto-runs the generation. The key pick is
 * remembered under `<storageKeyPrefix>:<provider>` (the same scheme the
 * editor chat reads), so the editor's picker pre-selects it on mount.
 */
export interface FlowGenerateModelPicker {
  /** Model pre-selected on mount (e.g. `'sonnet'`). */
  defaultModel: ChatModel;
  /** `localStorage` key prefix for remembering the last key pick. */
  storageKeyPrefix?: string;
  /** Href to the key-management page, shown when no keys exist. */
  addKeyHref?: string;
}

/** Resolve a model's provider so the key picker filters to it. */
function providerForModel(model: ChatModel): ModelProvider {
  return MODEL_MAP.get(model)?.provider ?? 'anthropic';
}

interface TemplateInfo {
  id: string;
  name: string;
  description: string;
  triggerLabel: string;
  icon: typeof Bot;
  color: string;
  bg: string;
  bgActive: string;
  border: string;
  borderActive: string;
  stats: { total: number; breakdown: string[] };
  create: () => FlowDefinition;
}

/**
 * Label per node type for the gallery card. Typed as `Record<FlowNodeType, …>`
 * so a newly added node type fails to compile here until it's labeled — the
 * card's node census can never silently fall behind the engine.
 */
const NODE_TYPE_LABELS: Record<FlowNodeType, { one: string; many: string }> = {
  agent: { one: 'agent', many: 'agents' },
  agent_task: { one: 'agent', many: 'agents' },
  approval: { one: 'approval', many: 'approvals' },
  router: { one: 'router', many: 'routers' },
  action: { one: 'action', many: 'actions' },
  filter: { one: 'filter', many: 'filters' },
  llm: { one: 'LLM', many: 'LLMs' },
  trigger: { one: 'trigger', many: 'triggers' },
};

/**
 * Summarize a template's graph for the gallery card: the true total node count
 * plus a per-type census (agent + agent_task collapse to "agents"). Every node
 * is labeled through the exhaustive {@link NODE_TYPE_LABELS} map, so the
 * breakdown always covers every type present and sums to `total`.
 */
function deriveStats(flow: FlowDefinition): TemplateInfo['stats'] {
  const byLabel = new Map<string, { one: string; many: string; n: number }>();
  for (const node of flow.nodes) {
    const { one, many } = NODE_TYPE_LABELS[node.type];
    const group = byLabel.get(many) ?? { one, many, n: 0 };
    group.n += 1;
    byLabel.set(many, group);
  }
  const order = [...new Set(Object.values(NODE_TYPE_LABELS).map((l) => l.many))];
  const breakdown = order
    .map((many) => byLabel.get(many))
    .filter((g): g is NonNullable<typeof g> => !!g && g.n > 0)
    .map((g) => `${g.n} ${g.n === 1 ? g.one : g.many}`);
  return { total: flow.nodes.length, breakdown };
}

/** Presentation chrome for one gallery template (everything but id/stats/create). */
type TemplateDisplay = Omit<TemplateInfo, 'id' | 'stats' | 'create'>;

/**
 * Display chrome per gallery template, keyed by catalog id. Typed
 * `Record<GalleryTemplateId, …>` so a new template without an entry here fails to
 * compile — the picker can't fall behind the catalog. Stats derive from the graph.
 */
const TEMPLATE_DISPLAY: Record<GalleryTemplateId, TemplateDisplay> = {
  'sentry-triage': {
    name: 'Sentry Triage',
    description:
      'Webhook-triggered error triage: summarize a Sentry issue, prompt the team to Investigate or Ignore, then analyze the error, implement a fix, and open a PR for review.',
    triggerLabel: 'Sentry Issue',
    icon: Bug,
    color: 'text-status-accent-orange',
    bg: 'hover:bg-status-accent-orange/5',
    bgActive: 'bg-status-accent-orange/10',
    border: 'border-transparent hover:border-status-accent-orange/20',
    borderActive: 'border-status-accent-orange/30',
  },
  'linear-dev': {
    name: 'Linear Dev Automation',
    description:
      'Status-driven dev lifecycle: when a Linear issue moves to "Ready to Implement", an agent plans the work for human approval, implements it behind a draft PR, self-reviews in a loop until clean, then marks the PR ready and the issue "In Review". Pick which assignees the trigger watches and set the target repo in each agent\'s Launch Configuration before saving.',
    triggerLabel: 'Linear Issue',
    icon: Workflow,
    color: 'text-status-accent-indigo',
    bg: 'hover:bg-status-accent-indigo/5',
    bgActive: 'bg-status-accent-indigo/10',
    border: 'border-transparent hover:border-status-accent-indigo/20',
    borderActive: 'border-status-accent-indigo/30',
  },
};

const TEMPLATES: TemplateInfo[] = GALLERY_TEMPLATES.map((t) => ({
  id: t.id,
  ...TEMPLATE_DISPLAY[t.id],
  stats: deriveStats(t.create(t.id)),
  create: () => t.create(`${t.id}-${Date.now()}`),
}));

const TEMPLATES_BY_ID = new Map(TEMPLATES.map((t) => [t.id, t]));

// --- Components ---

interface FlowTemplatePickerProps {
  onSelect: (flow: FlowDefinition) => void;
  onGenerate: (opts: { prompt: string; model: ChatModel }) => void;
  isGenerating?: boolean;
  /** Powers the model + provider-key pickers on the generate box. */
  modelPicker: FlowGenerateModelPicker;
  /**
   * Initial view for Storybook / tests. Landing no longer links to templates;
   * pass `'templates'` to exercise the browser without a CTA.
   */
  initialView?: 'landing' | 'templates';
}

type View = 'landing' | 'templates';

/** Shared trigger sizing for model + key pickers inside the generate input. */
const GENERATE_PICKER_TRIGGER_CLASS = 'h-auto max-w-60 px-2 py-1.5 text-xs font-medium';

/**
 * Horizontal workflow graph (node, connector, node) with a single spark
 * above the link. Stroke 2 on the lucide 24 grid matches Plus / ArrowUp
 * at `icon-md`; the spark stays 1.5 so it reads as an accent.
 */
function FlowGenerateIcon({ className }: { className?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className={cn('icon-md shrink-0', className)}
    >
      <rect width="7" height="7" x="2.5" y="8.5" rx="1.5" />
      <path d="M9.5 12h4" />
      <circle cx="17.5" cy="12" r="3.25" />
      <path d="M12 4.25v3.25M10.35 5.9h3.3" strokeWidth="1.5" />
    </svg>
  );
}

/**
 * If the leftover room beside the last chip is narrower than this, start the
 * prompt on the next line instead of squeezing a sliver of first-line text.
 */
const MIN_PROMPT_FIRST_LINE_PX = 48;

/**
 * A `<textarea>` cannot wrap around floated chips the way inline text can, so
 * chips overlay the prompt and `text-indent` / `padding-top` reserve the first
 * line (and extra chip rows) while wrapped lines still use the full width.
 */
function measureChipTextWrap(
  chipsEl: HTMLElement,
  wrapEl: HTMLElement,
): { paddingTop: number; textIndent: number } {
  const chips = [...chipsEl.children] as HTMLElement[];
  if (chips.length === 0) return { paddingTop: 0, textIndent: 0 };

  const wrapRect = wrapEl.getBoundingClientRect();
  const first = chips[0].getBoundingClientRect();
  const last = chips[chips.length - 1].getBoundingClientRect();
  const gap = Number.parseFloat(getComputedStyle(chipsEl).columnGap) || 6;
  const indent = last.right - wrapRect.left + gap;
  const remaining = wrapRect.width - indent;

  if (remaining < MIN_PROMPT_FIRST_LINE_PX) {
    return {
      paddingTop: Math.round(last.bottom - wrapRect.top + gap),
      textIndent: 0,
    };
  }

  return {
    paddingTop: Math.round(last.top - first.top),
    textIndent: Math.round(indent),
  };
}

/** Selected specification pill shown inside the generate prompt. */
function SpecChip({
  spec,
  onRemove,
  disabled,
}: {
  spec: Specification;
  onRemove: () => void;
  disabled?: boolean;
}) {
  const kindStyle = SPEC_KIND_ICONS[spec.kind];
  const KindIcon = kindStyle.icon;
  return (
    <span
      data-testid={`spec-chip-${spec.id}`}
      className="pointer-events-auto inline-flex max-w-full shrink-0 items-center gap-1.5 rounded-full bg-muted px-2 py-1 text-xs font-medium text-foreground"
    >
      {spec.connector ? (
        <PluginIcon
          icon={`plugin-icon-${spec.connector}.png`}
          size="sm"
          className="size-4 rounded-sm border-0"
          alt=""
        />
      ) : (
        <span
          className={cn('flex size-4 shrink-0 items-center justify-center rounded', kindStyle.bg)}
        >
          <KindIcon className={cn('size-2.5', kindStyle.color)} aria-hidden />
        </span>
      )}
      <span className="truncate">{spec.label}</span>
      <button
        type="button"
        aria-label={`Remove ${spec.label}`}
        onClick={onRemove}
        disabled={disabled}
        className="shrink-0 rounded-full p-0.5 text-muted-foreground hover:bg-background hover:text-foreground disabled:opacity-50"
      >
        <X className="size-3" aria-hidden />
      </button>
    </span>
  );
}

export function FlowTemplatePicker({
  onSelect,
  onGenerate,
  isGenerating,
  modelPicker,
  initialView = 'landing',
}: FlowTemplatePickerProps) {
  const [view, setView] = useState<View>(initialView);

  if (view === 'templates') {
    return <TemplateBrowser onSelect={onSelect} onBack={() => setView('landing')} />;
  }

  return (
    <NewFlowLanding onGenerate={onGenerate} isGenerating={isGenerating} modelPicker={modelPicker} />
  );
}

// --- Landing page ---

function NewFlowLanding({
  onGenerate,
  isGenerating,
  modelPicker,
}: {
  onGenerate: (opts: { prompt: string; model: ChatModel }) => void;
  isGenerating?: boolean;
  modelPicker: FlowGenerateModelPicker;
}) {
  const orgRoutes = useOrgRoutes();
  const [prompt, setPrompt] = useState('');
  const promptRef = useRef<HTMLTextAreaElement>(null);
  const promptWrapRef = useRef<HTMLDivElement>(null);
  const chipsRef = useRef<HTMLDivElement>(null);
  const [chipWrap, setChipWrap] = useState({ paddingTop: 0, textIndent: 0 });
  const [model, setModel] = useState<ChatModel>(modelPicker.defaultModel);
  const [providerSecretId, setProviderSecretId] = useState<string | null>(null);
  const [specs, setSpecs] = useState<Specification[]>([]);
  const [specsOpen, setSpecsOpen] = useState(false);
  const activeProvider = providerForModel(model);
  // Service-managed providers (e.g. Runloop) have no storable key rows; the
  // credential resolves server-side from the deployment's service secret, so
  // the key picker is hidden and generation is not gated on a pick.
  const providerHasStorableKeys = isPersistableModelProvider(activeProvider);

  // The default model is service-managed, so the key picker can't speak for it:
  // a deployment with the Runloop models plugin disabled (or missing its service
  // key) resolves no credential, and nothing else here would notice. Mirror the
  // chat composer and gate on the org's live model statuses instead.
  const { data: modelStatuses } = useEnabledModels();
  const enabledModelIds = useMemo((): Set<string> | undefined => {
    if (!modelStatuses) return undefined;
    return new Set<string>(modelStatuses.filter((s) => s.enabled).map((s) => s.id));
  }, [modelStatuses]);

  // Statuses loaded with zero enabled → no usable provider anywhere in the org.
  // (`undefined` while the query is in flight — don't flash the notice.)
  const noProviderConfigured = !!enabledModelIds && enabledModelIds.size === 0;

  // Fall off a default the org can't actually run onto its first enabled model.
  useEffect(() => {
    if (!enabledModelIds || enabledModelIds.size === 0) return;
    if (enabledModelIds.has(model)) return;
    const first = SELECTABLE_MODELS.find((m) => enabledModelIds.has(m.id));
    if (first) setModel(first.id);
  }, [enabledModelIds, model]);

  // Require an explicit key choice before generating — the picker
  // pre-selects the remembered/available key for the model's provider,
  // so this usually unblocks immediately. Matches the editor chat, which
  // gates sending on a key pick. Service-managed providers have no key to
  // pick, so they are exempt from the gate.
  const canGenerate =
    Boolean(prompt.trim()) &&
    !isGenerating &&
    !noProviderConfigured &&
    (providerHasStorableKeys ? !!providerSecretId : true);

  const handleGenerate = () => {
    if (!canGenerate) return;
    onGenerate({
      prompt: prompt.trim() + specificationsPromptSuffix(specs),
      model,
    });
  };

  const removeSpec = (id: string) => {
    setSpecs((prev) => prev.filter((s) => s.id !== id));
  };

  useLayoutEffect(() => {
    const chipsEl = chipsRef.current;
    const wrapEl = promptWrapRef.current;
    if (!chipsEl || !wrapEl || specs.length === 0) {
      setChipWrap({ paddingTop: 0, textIndent: 0 });
      return;
    }

    const measure = () => setChipWrap(measureChipTextWrap(chipsEl, wrapEl));
    measure();

    const observer = new ResizeObserver(measure);
    observer.observe(wrapEl);
    for (const chip of chipsEl.children) {
      observer.observe(chip);
    }
    return () => observer.disconnect();
  }, [specs]);

  return (
    <div className="h-full overflow-y-auto">
      <div className="mx-auto w-full max-w-7xl px-6 py-8 sm:px-8 sm:py-12">
        <Link
          to={orgRoutes.automations}
          className="mb-4 inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-3 w-3" aria-hidden="true" /> Back to automations
        </Link>
        <PageTitle className="font-semibold">Create Automation</PageTitle>
        <PageDescription>Build automated multi-step agent workflows</PageDescription>

        <div
          data-testid="flow-generate-card"
          className="mt-12 flex min-h-[200px] flex-col justify-between gap-3 rounded-xl border border-border bg-card p-4 shadow-sm"
        >
          <div className="flex items-center gap-2.5">
            <FlowGenerateIcon className="text-status-accent-indigo" />
            <div className="flex min-w-0 items-center gap-1.5">
              <PanelTitle as="h2" truncate={false} className="min-w-0 leading-none">
                Describe your workflow
              </PanelTitle>
              <Badge
                variant="secondary"
                className="bg-muted px-1.5 py-0 text-[10px] font-medium tracking-wide text-muted-foreground"
              >
                AI
              </Badge>
            </div>
          </div>

          <div
            ref={promptWrapRef}
            className="relative min-h-24 flex-1"
            data-testid="flow-generate-prompt-wrap"
            onClick={(e) => {
              if (e.target === e.currentTarget) promptRef.current?.focus();
            }}
          >
            {specs.length > 0 && (
              <div
                ref={chipsRef}
                className="pointer-events-none absolute inset-x-0 top-0 z-10 flex flex-wrap content-start items-center gap-1.5"
              >
                {specs.map((spec) => (
                  <SpecChip
                    key={spec.id}
                    spec={spec}
                    onRemove={() => removeSpec(spec.id)}
                    disabled={isGenerating}
                  />
                ))}
              </div>
            )}
            <Textarea
              ref={promptRef}
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder="i.e. When a Sentry issue comes in, have an agent analyze the error and propose a fix, then wait for human approval before opening a PR"
              rows={5}
              style={{
                textIndent: chipWrap.textIndent,
                paddingTop: chipWrap.paddingTop,
              }}
              className="min-h-24 w-full min-w-0 max-w-full resize-none rounded-none border-0 bg-transparent p-0 text-sm leading-6 shadow-none focus-visible:border-transparent focus-visible:ring-0 dark:bg-transparent"
              data-tutorial-id="flow-generate-prompt"
              disabled={isGenerating}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && (e.metaKey || e.ctrlKey) && canGenerate) {
                  e.preventDefault();
                  void handleGenerate();
                }
              }}
            />
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="rounded-full border border-status-accent-indigo/30 bg-status-accent-indigo/10 px-3 py-2 text-xs font-medium text-status-accent-indigo hover:bg-status-accent-indigo/20 hover:text-status-accent-indigo"
              aria-label="Add Trigger, Connectors, and Specs"
              onClick={() => setSpecsOpen(true)}
              disabled={isGenerating}
            >
              <Plus className="icon-md" aria-hidden="true" />
              Add Trigger, Connectors, and Specs…
            </Button>

            <div className="flex flex-wrap items-center gap-6">
              <div
                className="flex flex-wrap items-center gap-2"
                data-tutorial-id="flow-generate-model"
              >
                <ModelSelector
                  model={model}
                  onModelChange={setModel}
                  enabledModelIds={enabledModelIds}
                  compact
                  ghost
                  className={GENERATE_PICKER_TRIGGER_CLASS}
                />
                {providerHasStorableKeys && (
                  <>
                    <div className="h-5 w-px shrink-0 bg-border" aria-hidden="true" />
                    <ModelProviderSecretPicker
                      value={providerSecretId}
                      onChange={setProviderSecretId}
                      provider={activeProvider}
                      allowedTypes={['apiKey']}
                      storageKey={
                        modelPicker.storageKeyPrefix
                          ? `${modelPicker.storageKeyPrefix}:${activeProvider}`
                          : undefined
                      }
                      onEmptyAddHref={modelPicker.addKeyHref}
                      allowCreate
                      disabled={isGenerating}
                      ghost
                      className={cn(GENERATE_PICKER_TRIGGER_CLASS, 'max-w-60')}
                    />
                  </>
                )}
              </div>
              <Button
                size="icon-sm"
                onClick={() => void handleGenerate()}
                disabled={!canGenerate}
                aria-label="Generate automation"
                data-tutorial-id="flow-generate-submit"
              >
                {isGenerating ? (
                  <Loader2 className="icon-md animate-spin" />
                ) : (
                  <ArrowUp className="icon-md" />
                )}
              </Button>
            </div>
          </div>
        </div>

        <SpecificationPicker
          open={specsOpen}
          onOpenChange={setSpecsOpen}
          value={specs}
          onConfirm={setSpecs}
        />

        {noProviderConfigured && (
          <div
            role="status"
            data-testid="flow-generate-no-provider"
            className="mt-4 flex items-center justify-between gap-3 rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-sm text-amber-900 dark:text-amber-100"
          >
            <div className="flex items-start gap-2">
              <AlertTriangle className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
              <div>
                <p className="font-medium">No AI provider configured</p>
                <p className="text-xs opacity-80">
                  Generation needs a model this organization can run. Add a key in Model Providers.
                </p>
              </div>
            </div>
            {modelPicker.addKeyHref && (
              <Link
                to={modelPicker.addKeyHref}
                className="inline-flex shrink-0 items-center gap-1.5 rounded-md border border-amber-500/40 px-3 py-1.5 text-xs font-medium transition-colors hover:bg-amber-500/20"
              >
                <Settings2 className="size-3" />
                Model Providers
              </Link>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// --- Template browser (original picker, with back button) ---

/** A gallery template merged with its per-org availability from the server. */
type VisibleTemplate = TemplateInfo & {
  availability: 'usable' | 'preview';
  missing: FlowTemplateMissingPlugin[];
};

function TemplateBrowser({
  onSelect,
  onBack,
}: {
  onSelect: (flow: FlowDefinition) => void;
  onBack: () => void;
}) {
  const orgRoutes = useOrgRoutes();
  const orgKey = useOrgKey();
  const { data, isPending, isError } = useQuery({
    // Per-org availability: key on the active org so an org switch refetches.
    queryKey: ['flow-templates', orgKey],
    queryFn: () => flowsApi.getTemplates(),
  });

  // Merge server availability with local chrome/stats/create (server already
  // omits hidden). Fail open: on error, treat every template as usable.
  const visible = useMemo<VisibleTemplate[]>(() => {
    if (isError || !data) {
      return TEMPLATES.map((t) => ({ ...t, availability: 'usable' as const, missing: [] }));
    }
    return data.flatMap((entry) => {
      const display = TEMPLATES_BY_ID.get(entry.id);
      if (!display) return [];
      return [{ ...display, availability: entry.availability, missing: entry.missing ?? [] }];
    });
  }, [data, isError]);

  const [activeId, setActiveId] = useState<string>(TEMPLATES[0].id);
  const active = visible.find((t) => t.id === activeId) ?? visible[0];
  const previewFlow = useMemo(() => active?.create(), [active]);

  // Loading shows a spinner; resolved-but-empty shows an explicit empty state
  // (a spinner would never clear once the query has settled).
  if (isPending) {
    return <TemplateBrowserStatus onBack={onBack} kind="loading" />;
  }
  if (!active || !previewFlow) {
    return <TemplateBrowserStatus onBack={onBack} kind="empty" />;
  }

  const installHref =
    active.missing.length === 1
      ? orgRoutes.plugin(active.missing[0].pluginName)
      : orgRoutes.orgPlugins;

  return (
    <div className="flex h-full flex-col md:flex-row">
      {/* Left: Template list */}
      <div className="flex shrink-0 flex-col border-b md:w-72 md:border-b-0 md:border-r">
        <div className="border-b px-4 py-3">
          <TemplatesBackButton onBack={onBack} />
          <SectionLabel>Templates</SectionLabel>
          <p className="mt-0.5 text-xs text-muted-foreground">Select a template to preview</p>
        </div>

        <div className="flex-1 overflow-y-auto p-2">
          <div className="flex flex-col gap-1">
            {visible.map((tpl) => {
              const isActive = tpl.id === active.id;
              return (
                <button
                  key={tpl.id}
                  onClick={() => setActiveId(tpl.id)}
                  className={`flex items-start gap-3 rounded-lg border px-3 py-2.5 text-left transition-colors ${isActive ? `${tpl.bgActive} ${tpl.borderActive}` : `${tpl.bg} ${tpl.border}`}`}
                >
                  <tpl.icon className={`mt-0.5 h-4 w-4 shrink-0 ${tpl.color}`} />
                  <div className="min-w-0">
                    <div className="flex items-center gap-1.5">
                      <span className="text-sm font-medium leading-tight">{tpl.name}</span>
                      {tpl.availability === 'preview' && (
                        <span className="rounded bg-muted px-1 py-0.5 text-[9px] font-medium uppercase tracking-wide text-muted-foreground">
                          Preview
                        </span>
                      )}
                    </div>
                    <div className="mt-0.5 flex items-center gap-2 text-[10px] text-muted-foreground">
                      <span>{tpl.triggerLabel}</span>
                      <span>·</span>
                      <span>{tpl.stats.total} nodes</span>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Right: Preview */}
      <div className="flex flex-1 flex-col">
        {/* Preview header */}
        <div className="flex items-center justify-between border-b px-4 py-3">
          <div className="flex items-center gap-2.5">
            <active.icon className={`h-5 w-5 ${active.color}`} />
            <div>
              <PanelTitle as="h3" size="sm">
                {active.name}
              </PanelTitle>
              <span className="text-[11px] text-muted-foreground">
                {active.stats.breakdown.join(' · ')}
              </span>
            </div>
          </div>
          {active.availability === 'preview' ? (
            <div className="flex flex-col items-end gap-1">
              <Button size="sm" disabled className="gap-1.5">
                <Pencil className="h-3.5 w-3.5" />
                Use Template
              </Button>
              <Link
                to={installHref}
                className="text-[11px] text-status-accent-indigo hover:underline"
              >
                Requires {active.missing.map((m) => m.displayName).join(', ')}. Install to use.
              </Link>
            </div>
          ) : (
            <Button size="sm" onClick={() => onSelect(active.create())} className="gap-1.5">
              <Pencil className="h-3.5 w-3.5" />
              Use Template
            </Button>
          )}
        </div>

        {/* Graph preview */}
        <div className="flex-1">
          <FlowGraph
            key={active.id}
            nodes={previewFlow.nodes}
            edges={previewFlow.edges}
            autoLayout
          />
        </div>

        {/* Description footer */}
        <div className="border-t px-4 py-3">
          <p className="text-xs leading-relaxed text-muted-foreground">{active.description}</p>
        </div>
      </div>
    </div>
  );
}

function TemplatesBackButton({ onBack }: { onBack: () => void }) {
  return (
    <button
      type="button"
      onClick={onBack}
      className="mb-2 inline-flex items-center gap-1 text-xs text-muted-foreground transition-colors hover:text-foreground"
    >
      <ArrowLeft className="h-3 w-3" aria-hidden />
      Back to create
    </button>
  );
}

/** Left-rail shell shown while the availability query is loading, or when it resolved empty. */
function TemplateBrowserStatus({
  onBack,
  kind,
}: {
  onBack: () => void;
  kind: 'loading' | 'empty';
}) {
  return (
    <div className="flex h-full flex-col md:flex-row">
      <div className="flex shrink-0 flex-col border-b md:w-72 md:border-b-0 md:border-r">
        <div className="border-b px-4 py-3">
          <TemplatesBackButton onBack={onBack} />
          <SectionLabel>Templates</SectionLabel>
          <p className="mt-0.5 text-xs text-muted-foreground">
            {kind === 'loading' ? 'Loading templates' : 'No templates available'}
          </p>
        </div>
        <div className="flex flex-1 items-center justify-center p-6 text-muted-foreground">
          {kind === 'loading' ? <Loader2 className="h-5 w-5 animate-spin" /> : null}
        </div>
      </div>
      <div className="flex flex-1 items-center justify-center p-6 text-center text-muted-foreground">
        {kind === 'loading' ? (
          <Loader2 className="h-6 w-6 animate-spin" />
        ) : (
          <div className="max-w-xs">
            <LayoutGrid className="mx-auto mb-3 h-8 w-8 opacity-40" />
            <p className="text-sm">No templates are available for this organization.</p>
            <p className="mt-1 text-xs">
              Templates appear once their required plugins are available to your org.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
